import { useEffect, useMemo, useState } from "react";
import {
  getMetricDefinition,
  getSourceLoader,
  getUniqueSourceIds,
} from "../config/dashboardMetricsCatalog";

function extractMetricKeysFromLayout(layout) {
  if (!layout?.sections) return [];
  const metricKeys = [];

  layout.sections.forEach((section) => {
    if (section.type !== "metricGrid" || !Array.isArray(section.widgets)) return;

    section.widgets.forEach((widget) => {
      if (widget.metricKey) metricKeys.push(widget.metricKey);
      if (Array.isArray(widget.subMetrics)) {
        widget.subMetrics.forEach((subMetric) => {
          if (subMetric.metricKey) metricKeys.push(subMetric.metricKey);
        });
      }
    });
  });

  return [...new Set(metricKeys)];
}

export default function useDashboardMetrics(layout, { organizationId }) {
  const [metrics, setMetrics] = useState({});
  const [loading, setLoading] = useState(false);
  const [errorsBySource, setErrorsBySource] = useState({});

  const metricKeys = useMemo(() => extractMetricKeysFromLayout(layout), [layout]);
  const sourceIds = useMemo(() => getUniqueSourceIds(metricKeys), [metricKeys]);

  useEffect(() => {
    let isMounted = true;

    const loadMetrics = async () => {
      if (!organizationId || sourceIds.length === 0) {
        setMetrics({});
        setErrorsBySource({});
        setLoading(false);
        return;
      }

      setLoading(true);
      const nextMetrics = {};
      const nextErrors = {};

      const results = await Promise.allSettled(
        sourceIds.map(async (sourceId) => {
          const loader = getSourceLoader(sourceId);
          if (!loader) {
            throw new Error(`Unknown source loader: ${sourceId}`);
          }
          const sourceResult = await loader({ organizationId });
          return { sourceResult };
        }),
      );

      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          Object.assign(nextMetrics, result.value.sourceResult);
        } else {
          const failedSourceId = sourceIds[index];
          nextErrors[failedSourceId] = result.reason;
        }
      });

      metricKeys.forEach((metricKey) => {
        const definition = getMetricDefinition(metricKey);
        if (!definition) {
          nextMetrics[metricKey] = "--";
          return;
        }
        if (typeof nextMetrics[metricKey] === "undefined") {
          nextMetrics[metricKey] = "--";
        }
      });

      if (!isMounted) return;
      setMetrics(nextMetrics);
      setErrorsBySource(nextErrors);
      setLoading(false);
    };

    loadMetrics();

    return () => {
      isMounted = false;
    };
  }, [sourceIds, organizationId, metricKeys]);

  return { metrics, metricKeys, loading, errorsBySource };
}
